import {
  ExclamationCircle,
  CheckmarkCircle,
  ICircle,
  QuestionCircle,
} from '@squareup/dex-icons/market/alert';
import { NullableClassName } from '@squareup/dex-types-shared-ui';
import { Box, Paragraph30 } from '@squareup/dex-ui-shared-base';
import { commonIconStyles } from '@squareup/dex-ui-shared-icon-styles';
import clsx from 'clsx';
import React, { FC, PropsWithChildren, ReactNode } from 'react';

import styles from './aside.module.css';

type AsideType = 'info' | 'warning' | 'success' | 'important' | 'tip';

interface AsideProps {
  type: AsideType;
}

const asideTypeHeaderMap = new Map<AsideType, string>([
  ['info', 'Note'],
  ['warning', 'Warning'],
  ['success', 'Success'],
  ['important', 'Important'],
  ['tip', 'Did you know?'],
]);

const useAsideIcon = (type: AsideType): ReactNode => {
  let ResultIcon: FC<NullableClassName>;
  switch (type) {
    case 'important':
    case 'warning':
      ResultIcon = ExclamationCircle;
      break;
    case 'success':
      ResultIcon = CheckmarkCircle;
      break;
    case 'tip':
      ResultIcon = QuestionCircle;
      break;
    case 'info':
    default:
      ResultIcon = ICircle;
      break;
  }

  return (
    <ResultIcon
      className={clsx(styles.icon, commonIconStyles['icon-fill-current-color'])}
    />
  );
};

const Aside: FC<PropsWithChildren<AsideProps>> = ({ children, type }) => {
  const icon = useAsideIcon(type);
  return (
    <Box
      testId="aside"
      data-test-type={type}
      border={{ radius: 'standard' }}
      shadow="10"
      padding={{ vertical: '2x', horizontal: '2x' }}
      margin={{ vertical: '5x' }}
      className={clsx(styles.aside, styles[type])}
    >
      <Box margin={{ right: '1.5x' }}>{icon}</Box>
      <Box>
        <Paragraph30 weight="semi-bold" as="h4" margin={{ bottom: '1x' }}>
          {asideTypeHeaderMap.get(type)}
        </Paragraph30>
        {children}
      </Box>
    </Box>
  );
};

export { Aside, type AsideType };
